import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { ReactSortable } from 'react-sortablejs'

import { allCustomizableColumns, allCustomizableColumnsCar } from '../../../constants/defaultCustomizableColumns'
import { borderStyles, colors } from '../../../constants/salesStyles'
import { resetFilters } from '../../../hooks/partList/useResetFiltersOnUnmount'
import setUserActiveColumnsPartList from '../../../modules/redux/userSettings/SetUserActiveColumnsPartList'
import { useDispatch } from '../../../modules/store/customDispatch'
import { useSelector } from '../../../modules/store/customSelector'
import { useSetUserActiveColumnsCarListMutation } from '../../../modules/store/datahubApi/datahubApi'
import { Column } from '../../../modules/store/partList/partListReducer'
import { storeDispatchTypes } from '../../../modules/store/storeDispatchTypes'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import SalesButton from '../../_atoms/SalesButton/SalesButton'

import styles from './styles.module.scss'

interface IProps {
    isOpen: boolean
    closeModal: () => void
    customizableColumns: Column[]
    isCarColumns?: boolean
    availableColumns?: Column[]
    onSave?: (activeColumns: string[]) => void | Promise<void>
}

const ModalCustomizableColumns = ({
    isOpen,
    closeModal,
    customizableColumns,
    isCarColumns = false,
    availableColumns,
    onSave,
}: IProps) => {
    const dispatch = useDispatch()
    const isLoadingPartListCustomizableColumns = useSelector(state => state.partList.isLoading.customizableColumns)
    const [activeButtons, setActiveButtons] = useState<Column[]>([])
    const [additionalButtons, setAdditionalButtons] = useState<Column[]>([])

    const [setUserActiveColumnsCarList, { isLoading: isLoadingSetUserActiveColumnsCarList }] =
        useSetUserActiveColumnsCarListMutation()

    const isLoading = useMemo(
        () => isLoadingPartListCustomizableColumns || isLoadingSetUserActiveColumnsCarList,
        [isLoadingPartListCustomizableColumns, isLoadingSetUserActiveColumnsCarList],
    )

    const onCloseModal = useCallback(() => {
        closeModal()
    }, [closeModal])

    const confirm = useCallback(async () => {
        const activeColumns = activeButtons.map(column => column.id)
        if (onSave) {
            await onSave(activeColumns)
        } else if (isCarColumns) {
            await setUserActiveColumnsCarList({ activeColumns })
            dispatch({
                type: storeDispatchTypes.resetFiltersCars,
            })
        } else {
            await dispatch(setUserActiveColumnsPartList(activeColumns))
            resetFilters(dispatch)
        }

        onCloseModal()
    }, [activeButtons, dispatch, isCarColumns, onCloseModal, onSave, setUserActiveColumnsCarList])

    useEffect(() => {
        const columns = availableColumns || (isCarColumns ? allCustomizableColumnsCar : allCustomizableColumns)

        setActiveButtons(customizableColumns)
        setAdditionalButtons(
            columns.filter(column => !customizableColumns.some(defaultCol => defaultCol.id === column.id)),
        )
    }, [availableColumns, customizableColumns, isCarColumns])

    const getButton = (button: { id: string; label: string }, isActive: boolean) => (
        <div key={button.id}>
            <SalesButton
                className={styles.button}
                paddingHorizontalPixels={20}
                paddingVerticalPixels={5}
                marginBottomPixels={10}
                backgroundColor={colors.white}
                hoverBackgroundColor={colors.grayLighter}
                borderColor={isActive ? colors.greenLight : colors.grayBorder}
                borderWidth={1}
                borderStyle={borderStyles.solid}
                borderRadius={4}
            >
                <>{button.label}</>
            </SalesButton>
        </div>
    )

    return (
        <Modal show={isOpen} onHide={onCloseModal} centered dialogClassName={styles.modal}>
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Header closeButton>
                <Modal.Title>
                    <b>Настроить столбцы или изменить порядок</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={'row'}>
                    <div className={'col'}>
                        <h5>Активные</h5>
                    </div>
                    <div className={'col'}>
                        <h5>Дополнительные</h5>
                    </div>
                </div>
                <div className={styles.rowReactSortable}>
                    <ReactSortable group="shared" list={activeButtons} setList={setActiveButtons} animation={200}>
                        {activeButtons.map(button => getButton(button, true))}
                    </ReactSortable>
                    <ReactSortable
                        group="shared"
                        list={additionalButtons}
                        setList={setAdditionalButtons}
                        animation={200}
                    >
                        {additionalButtons.map(button => getButton(button, false))}
                    </ReactSortable>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={confirm}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ModalCustomizableColumns
